/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, FormText, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getSurveySections } from 'app/entities/survey-sections/survey-sections.reducer';
import { getEntities as getGlucoUsers } from 'app/entities/gluco-user/gluco-user.reducer';
import { getEntities as getPatientSurveys } from 'app/entities/patient-survey/patient-survey.reducer';
import { CompletedByType } from 'app/shared/model/enumerations/completed-by-type.model';
import { createEntity, getEntity, reset, updateEntity } from './patient-survey-sections.reducer';

export const PatientSurveySectionsUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const surveySections = useAppSelector(state => state.surveySections.entities);
  const glucoUsers = useAppSelector(state => state.glucoUser.entities);
  const patientSurveys = useAppSelector(state => state.patientSurvey.entities);
  const patientSurveySectionsEntity = useAppSelector(state => state.patientSurveySections.entity);
  const loading = useAppSelector(state => state.patientSurveySections.loading);
  const updating = useAppSelector(state => state.patientSurveySections.updating);
  const updateSuccess = useAppSelector(state => state.patientSurveySections.updateSuccess);
  const completedByTypeValues = Object.keys(CompletedByType);

  const handleClose = () => {
    navigate(`/patient-survey-sections${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getSurveySections({}));
    dispatch(getGlucoUsers({}));
    dispatch(getPatientSurveys({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    values.completedOn = convertDateTimeToServer(values.completedOn);
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastupdated !== undefined && typeof values.lastupdated !== 'number') {
      values.lastupdated = Number(values.lastupdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampentry !== undefined && typeof values.timestampentry !== 'number') {
      values.timestampentry = Number(values.timestampentry);
    }

    const entity = {
      ...patientSurveySectionsEntity,
      ...values,
      surveySection: surveySections.find(it => it.id.toString() === values.surveySection?.toString()),
      patient: glucoUsers.find(it => it.id.toString() === values.patient?.toString()),
      patientSurvey: patientSurveys.find(it => it.id.toString() === values.patientSurvey?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          completedOn: displayDefaultDateTime(),
        }
      : {
          completedByType: 'DOCTOR',
          ...patientSurveySectionsEntity,
          completedOn: convertDateTimeFromServer(patientSurveySectionsEntity.completedOn),
          surveySection: patientSurveySectionsEntity?.surveySection?.id,
          patient: patientSurveySectionsEntity?.patient?.id,
          patientSurvey: patientSurveySectionsEntity?.patientSurvey?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.patientSurveySections.home.createOrEditLabel" data-cy="PatientSurveySectionsCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.patientSurveySections.home.createOrEditLabel">
              Create or edit a PatientSurveySections
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="patient-survey-sections-id"
                  label={translate('tinyMedicRestApp.patientSurveySections.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurveySections.completedOn')}
                id="patient-survey-sections-completedOn"
                name="completedOn"
                data-cy="completedOn"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurveySections.completedByType')}
                id="patient-survey-sections-completedByType"
                name="completedByType"
                data-cy="completedByType"
                type="select"
              >
                {completedByTypeValues.map(completedByType => (
                  <option value={completedByType} key={completedByType}>
                    {translate(`tinyMedicRestApp.CompletedByType.${completedByType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurveySections.completedById')}
                id="patient-survey-sections-completedById"
                name="completedById"
                data-cy="completedById"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurveySections.active')}
                id="patient-survey-sections-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurveySections.cursor')}
                id="patient-survey-sections-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurveySections.lastupdated')}
                id="patient-survey-sections-lastupdated"
                name="lastupdated"
                data-cy="lastupdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurveySections.microSec')}
                id="patient-survey-sections-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurveySections.timestampentry')}
                id="patient-survey-sections-timestampentry"
                name="timestampentry"
                data-cy="timestampentry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                id="patient-survey-sections-surveySection"
                name="surveySection"
                data-cy="surveySection"
                label={translate('tinyMedicRestApp.patientSurveySections.surveySection')}
                type="select"
                required
              >
                <option value="" key="0" />
                {surveySections
                  ? surveySections.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="patient-survey-sections-patient"
                name="patient"
                data-cy="patient"
                label={translate('tinyMedicRestApp.patientSurveySections.patient')}
                type="select"
                required
              >
                <option value="" key="0" />
                {glucoUsers
                  ? glucoUsers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="patient-survey-sections-patientSurvey"
                name="patientSurvey"
                data-cy="patientSurvey"
                label={translate('tinyMedicRestApp.patientSurveySections.patientSurvey')}
                type="select"
              >
                <option value="" key="0" />
                {patientSurveys
                  ? patientSurveys.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/patient-survey-sections" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default PatientSurveySectionsUpdate;
